import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, Building2, Newspaper, ArrowRight, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { pickAirportBg } from "../../lib/assets";

/* "Aviation Pulse" strip on the Home page — three quick cards:
   featured Airlines, featured Airports and the latest Aviation news headlines. */

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function AviationPulseStrip() {
  const [airlines, setAirlines] = useState([]);
  const [airports, setAirports] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/airlines", { params: { limit: 6, active: "Y" } })
      .then((r) => setAirlines((r.data?.items || []).slice(0, 5)))
      .catch(() => setAirlines([]));
    api.get("/airports", { params: { limit: 6 } })
      .then((r) => setAirports((r.data?.items || r.data || []).slice(0, 5)))
      .catch(() => setAirports([]));
    api.get("/news", { params: { category: "aviation", limit: 4 } })
      .then((r) => setNews((r.data?.items || []).slice(0, 4)))
      .catch(() => setNews([]));
  }, []);

  return (
    <section className="grid md:grid-cols-3 gap-4" data-testid="home-aviation-pulse">
      {/* Airlines */}
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid="aviation-airlines">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 grid place-items-center"><Plane className="w-4 h-4" /></span>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-sky-700">Fly with</div>
              <div className="font-serif font-800 text-base text-blue-900 leading-tight">Featured Airlines</div>
            </div>
          </div>
          <Link to="/airlines" data-testid="aviation-airlines-more"
            className="text-blue-700 font-700 hover:underline text-xs inline-flex items-center gap-1">All <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <ul className="divide-y divide-slate-100">
          {airlines.length === 0 && <li className="p-5 text-sm text-slate-400">Loading airlines…</li>}
          {airlines.map((a) => (
            <li key={a.id || a.iata || a.name}>
              <Link to={`/airlines/${a.id || a.iata}`} data-testid={`aviation-airline-${(a.iata || a.id || "").toLowerCase()}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                <span className="w-10 h-10 rounded-lg bg-sky-50 text-sky-700 font-800 grid place-items-center text-xs shrink-0">
                  {(a.iata || a.icao || a.name || "").slice(0, 3).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-700 text-slate-900 truncate">{a.name}</div>
                  <div className="text-xs text-slate-500 truncate">{a.country || ""}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Airports */}
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid="aviation-airports">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 grid place-items-center"><Building2 className="w-4 h-4" /></span>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-blue-700">Fly from</div>
              <div className="font-serif font-800 text-base text-blue-900 leading-tight">Featured Airports</div>
            </div>
          </div>
          <Link to="/airports" data-testid="aviation-airports-more"
            className="text-blue-700 font-700 hover:underline text-xs inline-flex items-center gap-1">All <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <ul className="divide-y divide-slate-100">
          {airports.length === 0 && <li className="p-5 text-sm text-slate-400">Loading airports…</li>}
          {airports.map((a) => (
            <li key={a.id || a.iata_code || a.name}>
              <Link to={`/airports/${(a.iata_code || a.id || "").toUpperCase()}`}
                data-testid={`aviation-airport-${(a.iata_code || a.id || "").toLowerCase()}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                <span className="w-14 h-10 rounded-lg overflow-hidden shrink-0 relative">
                  <img src={pickAirportBg((a.iata_code || a.id || a.name) + (a.municipality || ""))} alt=""
                    className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-700 text-slate-900 truncate">
                    {a.iata_code || a.id} — {a.municipality || a.iso_country || ""}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{a.name}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Aviation News */}
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid="aviation-news">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 grid place-items-center"><Newspaper className="w-4 h-4" /></span>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-amber-700">Right now</div>
              <div className="font-serif font-800 text-base text-blue-900 leading-tight">Latest Aviation News</div>
            </div>
          </div>
          <Link to="/news" data-testid="aviation-news-more"
            className="text-blue-700 font-700 hover:underline text-xs inline-flex items-center gap-1">All <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <ul className="divide-y divide-slate-100">
          {news.length === 0 && <li className="p-5 text-sm text-slate-400">Fetching latest headlines…</li>}
          {news.map((n) => (
            <li key={n.id}>
              <a href={n.link} target="_blank" rel="noopener noreferrer" data-testid={`aviation-news-${n.id}`}
                className="flex items-start gap-3 px-5 py-3 hover:bg-slate-50">
                <ExternalLink className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-700 text-slate-900 leading-snug line-clamp-2">{n.title}</div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate">
                    {n.source} · {timeAgo(n.published)}
                  </div>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
